import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Schema for API key in list response
 */
const ApiKeySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  keyPrefix: z.string(),
  lastFourChars: z.string(),
  maskedKey: z.string(),
  tenantScoped: z.boolean(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  lastUsedAt: z.string().nullable(),
  usageCount: z.number(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Create API key input schema
 */
const CreateApiKeyInputSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  tenantScoped: z.boolean().optional(),
});

/**
 * Create API key response schema
 */
const CreateApiKeyResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  keyPrefix: z.string(),
  lastFourChars: z.string(),
  createdAt: z.string(),
  fullKey: z.string(), // Only returned once at creation
});

/**
 * Revoke API key input schema
 */
const RevokeApiKeyInputSchema = z.object({
  id: z.string().uuid("Invalid key ID format"),
});

/**
 * API Keys Contracts
 */
export const apiKeysContracts = {
  // List all API keys for the organization
  list: oc.output(z.object({ apiKeys: z.array(ApiKeySchema) })).route({
    method: "GET",
    summary: "List API keys",
    description:
      "Lists your organization's API keys, masked, with creator, last use, usage count and revoked status.",
  }),

  // Create a new API key
  create: oc
    .input(CreateApiKeyInputSchema)
    .output(CreateApiKeyResponseSchema)
    .route({
      method: "POST",
      summary: "Create an API key",
      description:
        "Generates a new API key for your organization, optionally scoped to the whole tenant. The full key is returned only once; creation is audit-logged.",
    }),

  // Revoke an API key
  revoke: oc
    .input(RevokeApiKeyInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Revoke an API key",
      description:
        "Revokes an active API key so it can no longer authenticate requests. The revocation is audit-logged.",
    }),
} as const;

// Export types for frontend use
export type ApiKeyDTO = z.infer<typeof ApiKeySchema>;
export type CreateApiKeyInput = z.infer<typeof CreateApiKeyInputSchema>;
export type CreateApiKeyResponse = z.infer<typeof CreateApiKeyResponseSchema>;
